import type { CSSProperties } from 'react';
import { z } from 'zod';
import { configuredOr } from '@/lib/configValue';

/**
 * The one implementation of the `{ fontSize, fontColor, fontFamily, fontWeight }` -> CSS
 * mapping that the legacy app copy-pasted into sixteen files (docs/MIGRATION-WIDGETS.md §2).
 *
 * Roughly 400 of the legacy app's ~2500 widget lines are that one shape, with a different
 * default `fontSize` per call site and everything else identical. Here the differing part is
 * a `defaults` argument and the shape itself exists once.
 *
 * ```ts
 * const style = fontStyleToCss(options.textBlocFont, { fontSize: '1.2rem' });
 * ```
 */

/**
 * `fontSize` is `number | string` and both forms are in the deployed configs.
 *
 * - Most widgets carry a **number** (`1.5`) and the legacy code appended `'rem'`.
 * - `BookingsWidget`'s `options.component.{columnLabel,rowLabel,cellLabel}` carry a
 *   **pre-united string** (`"1.2rem"`) and the legacy code appended nothing.
 *
 * Getting this wrong yields `1.2remrem` (no text at all) or `1.2` (a 1.2 *pixel* caption),
 * on one widget only — see the unit trap in docs/MIGRATION-WIDGETS.md §2.
 */
export const fontOptionsSchema = z.object({
  /** Number -> `rem` appended. String -> already united, passed through. */
  fontSize: z.union([z.number(), z.string()]).optional(),
  /** Maps to CSS `color`, not to `font-color`. */
  fontColor: z.string().optional(),
  fontFamily: z.string().optional(),
  fontWeight: z.string().optional(),
});

export type FontOptions = z.infer<typeof fontOptionsSchema>;

/**
 * What a call site falls back to when config supplies nothing.
 *
 * `fontSize` is required because it is the only one that genuinely differs per component —
 * `1.2rem` in `textBloc`, `2rem` in the three title blocks, `0.5rem` in `imageLabelWidget`,
 * `0.8rem` in `Pin`, and so on (the table in docs/MIGRATION-WIDGETS.md §2). Passing it
 * explicitly at every call site is what stops that table from silently collapsing to one
 * value.
 *
 * It is a **pre-united string** (`'1.2rem'`), never a number: a default is written by us, in
 * code, and does not need the config-compatibility unit dance above.
 */
export type FontDefaults = {
  /** Pre-united, e.g. `'1.2rem'`. */
  fontSize: string;
  /** CSS `color`. `'white'` everywhere in the legacy app, which is the default here. */
  fontColor?: string;
  /** `'normal'` everywhere in the legacy app, which is the default here. */
  fontWeight?: string;
};

/**
 * Font options from config -> a React style object.
 *
 * Three behaviours are load-bearing and each is a legacy detail rather than a preference:
 *
 * 1. **Falsy is absent.** The legacy code tested `(font.fontSize) ? … : default`, so `0` and
 *    `''` fall back to the default rather than rendering `0rem` or an empty declaration.
 *    One deployed config carries `"family": ""` and relies on it (`imageLabelWidget`).
 * 2. **A number gets `rem`, a string does not** — the unit trap above.
 * 3. **No `fontFamily` means no declaration.** Legacy called `getDefaultFontFamily()`, which
 *    read `getComputedStyle(document.body).fontFamily` and wrote it straight back as an
 *    inline style. Omitting the property inherits the `* { font-family }` rule in
 *    `global.css` instead, so the function is deliberately not ported.
 *
 *    **This does *not* render identically, contrary to what this comment used to claim.**
 *    Measured by SYM-506 against the built legacy app: `body`'s computed family there is
 *    `Arial, sans-serif`, because Tailwind 2's preflight ships `body { font-family: inherit }`
 *    and `0,0,1` beats `*`'s `0,0,0`. So legacy copies *Arial* onto every element that goes
 *    through a font block, while these elements inherit `HubhubSans-Medium`. It is a visible
 *    typeface change on all seven deployed displays — recorded as defect **QA-1** in
 *    `docs/QA-PARITY.md` and decision **#22** in `docs/MIGRATION.md`, deliberately left
 *    unchanged pending sign-off rather than "fixed" one way or the other by an agent.
 */
export function fontStyleToCss(font: FontOptions | undefined, defaults: FontDefaults): CSSProperties {
  // `configuredOr`, not `??`: the legacy check is truthiness, so a configured `''` falls back
  // to the default rather than emitting an empty declaration. `??` on the *defaults* is
  // correct — those are written in code and are never the empty string.
  const style: CSSProperties = {
    fontSize: toCssFontSize(font?.fontSize) ?? defaults.fontSize,
    color: configuredOr(font?.fontColor, defaults.fontColor ?? 'white'),
    fontWeight: configuredOr(font?.fontWeight, defaults.fontWeight ?? 'normal'),
  };

  // Assigned conditionally rather than as `fontFamily: … ?? undefined`: React skips
  // `undefined` style values, but keeping the key out entirely also keeps it out of the
  // object identity comparisons every widget below this does on its style prop.
  const fontFamily = font?.fontFamily;
  if (fontFamily) style.fontFamily = fontFamily;

  return style;
}

/**
 * `1.5` -> `'1.5rem'`, `'1.2rem'` -> `'1.2rem'`, `0` / `''` / `undefined` -> `undefined`.
 *
 * Exported because `BookingsWidget` (SYM-501) applies the same rule to `lineHeight`, `width`
 * and `height`, which arrive alongside its font options in the same pre-united form.
 */
export function toCssFontSize(size: number | string | undefined): string | undefined {
  if (!size) return undefined;
  return typeof size === 'number' ? `${String(size)}rem` : size;
}
